import { Box, InputAdornment } from '@mui/material';
import useMobile from 'src/shared/hooks/useMobile';
import { TextField } from 'mui-rff';
import { FormContentSection } from '../CategoriesGroup/Form/FormContentSection';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import GroupHeader from '../GroupHeader';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';

type FlexibleRetributionWalletGroupProps = {
  currencySymbol: string;
  allocationEnabled?: boolean;
  frWalletTotals?: {
    totalAccumulated: { food: number; transport: number };
    totalSpent: { food: number; transport: number };
    currentlyAvailable: { food: number; transport: number };
  };
  settings?: {
    frWalletAccrualEnabled: boolean;
    frWalletFoodAccrual: number;
    frWalletTransportAccrual: number;
    frWalletFoodAccrualMaxEnabled?: boolean;
    frWalletTransportAccrualMaxEnabled?: boolean;
    frWalletFoodAccrualPercentage?: number;
    frWalletTransportAccrualPercentage?: number;
  };
};

const FlexibleRetributionWalletGroup = ({
  currencySymbol,
  allocationEnabled = true,
  frWalletTotals,
  settings
}: FlexibleRetributionWalletGroupProps) => {
  const classes = useStyles();
  const isMobile = useMobile();
  const { t } = useTranslation(['flexflow']);

  const isNonAllocationMode = !allocationEnabled;

  const inputProps = (startSymbol: string) => ({
    startAdornment: <InputAdornment position="start">{startSymbol}</InputAdornment>,
    endAdornment: (
      <InputAdornment position="end">
        <LockOutlinedIcon fontSize="small" sx={{ opacity: 0.4 }} />
      </InputAdornment>
    )
  });

  const accumulatedFood = frWalletTotals?.currentlyAvailable?.food ?? 0;
  const accumulatedTransport = frWalletTotals?.currentlyAvailable?.transport ?? 0;

  const configFood = isNonAllocationMode
    ? settings?.frWalletFoodAccrualPercentage ?? 0
    : settings?.frWalletFoodAccrual ?? 0;

  const configTransport = isNonAllocationMode
    ? settings?.frWalletTransportAccrualPercentage ?? 0
    : settings?.frWalletTransportAccrual ?? 0;

  return (
    <Grid2 container>
      <GroupHeader className={classes.tooltipWrapper} marginTop={32}>
        {t('walletAccumulated.title', { ns: 'flexflow' })}
      </GroupHeader>

      <Grid2 mb={4} xs={12} md={10} lg={6}>
        <div>
          <Grid2 xs={12} sx={{ w: '100%', maxWidth: '600px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormContentSection
                isMobile={isMobile}
                title={t('walletAccumulated.balance.title', { ns: 'flexflow' })}
                description={t('walletAccumulated.balance.description', { ns: 'flexflow' })}
              >
                <TextField
                  disabled
                  label={t('transactions.categories.food', { ns: 'flexflow' })}
                  name="accumulatedFood"
                  value={accumulatedFood}
                  InputProps={inputProps(currencySymbol)}
                  placeholder="-"
                />
                <TextField
                  disabled
                  label={t('transactions.categories.transport', { ns: 'flexflow' })}
                  name="accumulatedTransport"
                  value={accumulatedTransport}
                  InputProps={inputProps(currencySymbol)}
                  placeholder="-"
                />
              </FormContentSection>
              <FormContentSection
                isMobile={isMobile}
                title={t('walletAccumulated.configuration.title', { ns: 'flexflow' })}
                description={t('walletAccumulated.configuration.description', { ns: 'flexflow' })}
              >
                <TextField
                  disabled
                  label={t('transactions.categories.food', { ns: 'flexflow' })}
                  name="configFood"
                  value={configFood}
                  InputProps={inputProps(isNonAllocationMode ? '%' : currencySymbol)}
                  placeholder="-"
                />
                <TextField
                  disabled
                  label={t('transactions.categories.transport', { ns: 'flexflow' })}
                  name="configTransport"
                  value={configTransport}
                  InputProps={inputProps(isNonAllocationMode ? '%' : currencySymbol)}
                  placeholder="-"
                />
              </FormContentSection>
            </Box>
          </Grid2>
        </div>
      </Grid2>
    </Grid2>
  );
};

export default FlexibleRetributionWalletGroup;
